import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  Building2, Target, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp,
  ArrowDown, BarChart3, PieChart, Eye, Users, Briefcase, Activity,
  Scale, Landmark, FileText, Globe, Megaphone
} from 'lucide-react';
import './LobbyingPage.css';

// =============================================================================
// MOCK DATA — structured to match Quiver /beta/historical/lobbying endpoint
// Fields: Ticker, Client, Amount, Issue, SpecificIssue, Registrant, Date
// =============================================================================

const mockLobbyingData = [
  { id: 1, ticker: 'AAPL', client: 'Apple Inc.', registrant: 'Fierce Government Relations', amount: 9870000, issue: 'Taxation', specificIssue: 'Corporate tax reform, digital services tax, OECD global minimum tax framework', filingDate: '2026-02-06', quarter: 'Q4 2025', year: 2025 },
  { id: 2, ticker: 'GOOGL', client: 'Alphabet Inc.', registrant: 'Franklin Square Group', amount: 13200000, issue: 'Telecommunications', specificIssue: 'Section 230 reform, AI regulation, antitrust compliance, digital advertising transparency', filingDate: '2026-02-05', quarter: 'Q4 2025', year: 2025 },
  { id: 3, ticker: 'META', client: 'Meta Platforms Inc.', registrant: 'Lot Sixteen LLC', amount: 19480000, issue: 'Communications/Broadcasting', specificIssue: 'Social media content moderation, child safety legislation, VR/AR regulation, data privacy', filingDate: '2026-02-05', quarter: 'Q4 2025', year: 2025 },
  { id: 4, ticker: 'AMZN', client: 'Amazon.com Services LLC', registrant: 'Akin Gump Strauss Hauer & Feld', amount: 21380000, issue: 'Trade', specificIssue: 'Antitrust legislation, labor regulations, drone delivery, AWS government contracts', filingDate: '2026-02-04', quarter: 'Q4 2025', year: 2025 },
  { id: 5, ticker: 'MSFT', client: 'Microsoft Corporation', registrant: 'Invariant LLC', amount: 10800000, issue: 'Science/Technology', specificIssue: 'AI safety framework, cybersecurity standards, cloud computing FedRAMP, gaming regulation', filingDate: '2026-02-04', quarter: 'Q4 2025', year: 2025 },
  { id: 6, ticker: 'NVDA', client: 'NVIDIA Corporation', registrant: 'Brownstein Hyatt Farber Schreck', amount: 5240000, issue: 'Science/Technology', specificIssue: 'AI chip export controls, semiconductor subsidies CHIPS Act implementation, data center energy', filingDate: '2026-02-03', quarter: 'Q4 2025', year: 2025 },
  { id: 7, ticker: 'BA', client: 'The Boeing Company', registrant: 'Squire Patton Boggs', amount: 12640000, issue: 'Defense', specificIssue: 'Defense procurement, FAA certification reform, NASA contracts, export licensing', filingDate: '2026-02-03', quarter: 'Q4 2025', year: 2025 },
  { id: 8, ticker: 'PFE', client: 'Pfizer Inc.', registrant: 'Sidley Austin LLP', amount: 11200000, issue: 'Health Issues', specificIssue: 'Drug pricing reform, Medicare Part D, FDA approval acceleration, patent protection', filingDate: '2026-02-02', quarter: 'Q4 2025', year: 2025 },
  { id: 9, ticker: 'JPM', client: 'JPMorgan Chase & Co.', registrant: 'Davis Polk & Wardwell', amount: 8920000, issue: 'Banking', specificIssue: 'Basel III endgame capital requirements, digital assets regulation, CFPB oversight, ESG disclosure', filingDate: '2026-02-01', quarter: 'Q4 2025', year: 2025 },
  { id: 10, ticker: 'LMT', client: 'Lockheed Martin Corp.', registrant: 'Akin Gump Strauss Hauer & Feld', amount: 14350000, issue: 'Defense', specificIssue: 'F-35 program funding, hypersonic weapons development, space defense contracts, Ukraine aid packages', filingDate: '2026-01-31', quarter: 'Q4 2025', year: 2025 },
  { id: 11, ticker: 'UNH', client: 'UnitedHealth Group', registrant: 'Forbes Tate Partners', amount: 8450000, issue: 'Health Issues', specificIssue: 'Medicare Advantage reimbursement, PBM transparency, telehealth expansion, prior authorization reform', filingDate: '2026-01-30', quarter: 'Q4 2025', year: 2025 },
  { id: 12, ticker: 'XOM', client: 'Exxon Mobil Corp.', registrant: 'BGR Group', amount: 7680000, issue: 'Energy/Nuclear', specificIssue: 'Carbon capture tax credits, LNG export permits, methane regulations, offshore drilling leases', filingDate: '2026-01-29', quarter: 'Q4 2025', year: 2025 },
  { id: 13, ticker: 'T', client: 'AT&T Inc.', registrant: 'Wiley Rein LLP', amount: 6120000, issue: 'Telecommunications', specificIssue: 'Broadband subsidies, spectrum allocation, 6G research funding, rural connectivity expansion', filingDate: '2026-01-28', quarter: 'Q4 2025', year: 2025 },
  { id: 14, ticker: 'CVS', client: 'CVS Health Corp.', registrant: 'Tarplin Downs & Young', amount: 5890000, issue: 'Health Issues', specificIssue: 'PBM regulation, pharmacy benefit reform, biosimilar coverage, opioid settlement compliance', filingDate: '2026-01-27', quarter: 'Q4 2025', year: 2025 },
  { id: 15, ticker: 'RTX', client: 'RTX Corporation', registrant: 'Holland & Knight', amount: 9240000, issue: 'Defense', specificIssue: 'Patriot missile system funding, engine sustainment contracts, NATO alliance procurement', filingDate: '2026-01-26', quarter: 'Q4 2025', year: 2025 },
  { id: 16, ticker: 'GM', client: 'General Motors Co.', registrant: 'Van Scoyoc Associates', amount: 4560000, issue: 'Automotive', specificIssue: 'EV tax credit eligibility, autonomous vehicle regulation, CAFE standards, UAW labor provisions', filingDate: '2026-01-25', quarter: 'Q4 2025', year: 2025 },
  { id: 17, ticker: 'CMCSA', client: 'Comcast Corporation', registrant: 'Comcast Corporation', amount: 7350000, issue: 'Telecommunications', specificIssue: 'Net neutrality, broadband competition, media ownership rules, streaming regulation', filingDate: '2026-01-24', quarter: 'Q4 2025', year: 2025 },
  { id: 18, ticker: 'GS', client: 'Goldman Sachs Group', registrant: 'Sullivan & Cromwell', amount: 5670000, issue: 'Banking', specificIssue: 'Capital markets regulation, private credit oversight, wealth management fiduciary rules', filingDate: '2026-01-23', quarter: 'Q4 2025', year: 2025 },
  { id: 19, ticker: 'WMT', client: 'Walmart Inc.', registrant: 'Prime Policy Group', amount: 6780000, issue: 'Trade', specificIssue: 'Tariff exemptions, supply chain resilience act, minimum wage, retail workforce regulations', filingDate: '2026-01-22', quarter: 'Q4 2025', year: 2025 },
  { id: 20, ticker: 'TSLA', client: 'Tesla Inc.', registrant: 'Todd Strategy LLC', amount: 3240000, issue: 'Energy/Nuclear', specificIssue: 'EV infrastructure credits, autonomous driving framework, energy storage incentives, solar ITC', filingDate: '2026-01-21', quarter: 'Q4 2025', year: 2025 },
];

// Top spenders (annual)
const mockTopSpenders = [
  { ticker: 'AMZN', name: 'Amazon.com', annual: 21380000, change: 12.4 },
  { ticker: 'META', name: 'Meta Platforms', annual: 19480000, change: 8.7 },
  { ticker: 'LMT', name: 'Lockheed Martin', annual: 14350000, change: -2.1 },
  { ticker: 'GOOGL', name: 'Alphabet Inc.', annual: 13200000, change: 15.2 },
  { ticker: 'BA', name: 'Boeing Co.', annual: 12640000, change: 6.8 },
];

// Spending by issue
const mockIssueBreakdown = [
  { issue: 'Defense', amount: 36230000, count: 3, color: '#ef4444' },
  { issue: 'Health Issues', amount: 25540000, count: 3, color: '#10b981' },
  { issue: 'Science/Technology', amount: 16040000, count: 2, color: '#8b5cf6' },
  { issue: 'Telecommunications', amount: 26670000, count: 3, color: '#3b82f6' },
  { issue: 'Trade', amount: 28160000, count: 2, color: '#f59e0b' },
  { issue: 'Banking', amount: 14590000, count: 2, color: '#06b6d4' },
  { issue: 'Energy/Nuclear', amount: 10920000, count: 2, color: '#f97316' },
  { issue: 'Taxation', amount: 9870000, count: 1, color: '#ec4899' },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const StatCard = ({ icon: Icon, label, value, sub, subType, color }) => (
  <div className="lb-stat-card">
    <div className="lb-stat-icon" style={{ background: color }}>
      <Icon size={18} color="#fff" />
    </div>
    <div className="lb-stat-label">{label}</div>
    <div className="lb-stat-value">{value}</div>
    <div className={`lb-stat-sub ${subType}`}>{sub}</div>
  </div>
);

const AIInsightCard = ({ insight }) => (
  <div className="lb-ai-insight">
    <div className="lb-ai-insight-header">
      <span className="lb-ai-badge"><Brain size={12} /> AI INSIGHT</span>
      <span>Updated 4 hours ago</span>
    </div>
    <p dangerouslySetInnerHTML={{ __html: insight }} />
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function LobbyingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [issueFilter, setIssueFilter] = useState('All');
  const [sortBy, setSortBy] = useState('filingDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const issueFilters = ['All', 'Defense', 'Health', 'Tech', 'Telecom', 'Trade', 'Banking', 'Energy'];

  const getIssueMatch = (issue, filter) => {
    if (filter === 'All') return true;
    if (filter === 'Health') return issue.includes('Health');
    if (filter === 'Tech') return issue.includes('Science') || issue.includes('Technology');
    if (filter === 'Telecom') return issue.includes('Telecom') || issue.includes('Commun');
    return issue.toLowerCase().includes(filter.toLowerCase());
  };

  // Stats
  const stats = useMemo(() => {
    const totalSpending = mockLobbyingData.reduce((s, d) => s + d.amount, 0);
    const uniqueClients = new Set(mockLobbyingData.map(d => d.client)).size;
    const avgSpend = totalSpending / uniqueClients;
    const topIssue = 'Defense';
    return { totalSpending, uniqueClients, avgSpend, topIssue };
  }, []);

  // Filter + Sort
  const filteredData = useMemo(() => {
    let data = [...mockLobbyingData];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(d =>
        d.client.toLowerCase().includes(q) ||
        d.ticker.toLowerCase().includes(q) ||
        d.registrant.toLowerCase().includes(q) ||
        d.issue.toLowerCase().includes(q)
      );
    }
    if (issueFilter !== 'All') {
      data = data.filter(d => getIssueMatch(d.issue, issueFilter));
    }
    data.sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [searchTerm, issueFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredData.length / perPage);
  const pagedData = filteredData.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const aiInsight = `<span class="highlight">Corporate lobbying hit $4.1B in 2025</span>, a 6.2% increase year-over-year. <span class="highlight">Amazon</span> leads tech spending at $21.4M, driven by antitrust and labor lobbying. <span class="highlight">AI regulation</span> is the fastest-growing lobbying issue (+340% since 2023), with NVDA, MSFT, and GOOGL dramatically increasing spend on export controls and safety frameworks. Defense contractors maintain the highest per-company spend, with LMT ($14.3M) and RTX ($9.2M) focused on next-gen weapons procurement. Healthcare lobbying is intensifying around PBM transparency legislation.`;

  const maxIssueAmount = Math.max(...mockIssueBreakdown.map(i => i.amount));

  return (
    <div className="lobbying-page">
      {/* Header */}
      <div className="lb-page-header">
        <div className="lb-header-content">
          <div className="lb-header-title">
            <div className="lb-header-icon"><Scale size={22} color="#fff" /></div>
            <div>
              <h1>Lobbying Data</h1>
              <p>Corporate lobbying expenditures • LDA filings & registrant disclosures</p>
            </div>
          </div>
          <div className="lb-header-actions">
            <button className="lb-header-btn"><Download size={14} /> Export</button>
            <button className="lb-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="lb-stats-row">
        <StatCard icon={DollarSign} label="Total Lobbying Spend (Q4)" value={formatCurrency(stats.totalSpending)} sub="20 filings tracked" subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={Building2} label="Active Lobbying Clients" value={stats.uniqueClients.toString()} sub="Major corporations" subType="neutral" color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={TrendingUp} label="Avg Spend per Client" value={formatCurrency(stats.avgSpend)} sub="+6.2% vs last year" subType="positive" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={Landmark} label="Top Issue Category" value={stats.topIssue} sub="$36.2M in defense lobbying" subType="neutral" color="linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="lb-content-grid">
        {/* Main Table Panel */}
        <div className="lb-table-panel">
          <div className="lb-panel-header">
            <h2><FileText size={16} /> Recent Lobbying Filings <span className="lb-record-count">{filteredData.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="lb-toolbar">
            <div className="lb-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search company, ticker, or issue..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="lb-filter-toggles">
              {issueFilters.map(f => (
                <button key={f} className={`lb-filter-btn ${issueFilter === f ? 'active' : ''}`} onClick={() => { setIssueFilter(f); setCurrentPage(1); }}>{f}</button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="lb-table-wrap">
            <table className="lb-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('ticker')}>Stock <SortIcon field="ticker" /></th>
                  <th>Client</th>
                  <th onClick={() => handleSort('amount')}>Amount <SortIcon field="amount" /></th>
                  <th>Issue</th>
                  <th>Registrant</th>
                  <th onClick={() => handleSort('filingDate')}>Filed <SortIcon field="filingDate" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedData.map(filing => (
                  <tr key={filing.id} className="lb-row" onClick={() => navigate(`/lobbying/${filing.ticker}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <span className="lb-ticker">{filing.ticker}</span>
                    </td>
                    <td>
                      <div className="lb-client-name">{filing.client}</div>
                    </td>
                    <td>
                      <span className="lb-amount">{formatCurrency(filing.amount)}</span>
                    </td>
                    <td>
                      <span className="lb-issue-badge">{filing.issue}</span>
                    </td>
                    <td>
                      <div className="lb-registrant">{filing.registrant}</div>
                    </td>
                    <td>{new Date(filing.filingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      <button className="lb-view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/stock/${filing.ticker}?tab=government`); }} title={`View ${filing.ticker}`}>
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="lb-pagination">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lb-sidebar">
          {/* Spending by Issue */}
          <div className="lb-sidebar-card">
            <h3><PieChart size={16} /> Spending by Issue</h3>
            <div className="lb-issue-list">
              {mockIssueBreakdown.sort((a, b) => b.amount - a.amount).map((item, idx) => (
                <div key={idx} className="lb-issue-row">
                  <div className="lb-issue-info">
                    <div className="lb-issue-dot" style={{ background: item.color }}></div>
                    <span className="lb-issue-name">{item.issue}</span>
                  </div>
                  <div className="lb-issue-bar-bg">
                    <div className="lb-issue-bar" style={{ width: `${(item.amount / maxIssueAmount) * 100}%`, background: item.color }}></div>
                  </div>
                  <div className="lb-issue-stats">
                    <span className="lb-issue-amount">{formatCurrency(item.amount)}</span>
                    <span className="lb-issue-count">{item.count} filers</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Spenders */}
          <div className="lb-sidebar-card">
            <h3><Megaphone size={16} /> Top Spenders</h3>
            <div className="lb-top-list">
              {mockTopSpenders.map((s, idx) => (
                <div key={idx} className="lb-top-item" onClick={() => navigate(`/stock/${s.ticker}?tab=government`)}>
                  <span className="lb-top-rank">{idx + 1}</span>
                  <div className="lb-top-info">
                    <span className="lb-top-ticker">{s.ticker}</span>
                    <span className="lb-top-name">{s.name}</span>
                  </div>
                  <div className="lb-top-stats">
                    <span className="lb-top-amount">{formatCurrency(s.annual)}</span>
                    <span className={`lb-top-change ${s.change >= 0 ? 'positive' : 'negative'}`}>{s.change >= 0 ? '+' : ''}{s.change}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Facts */}
          <div className="lb-sidebar-card">
            <h3><BarChart3 size={16} /> Quick Facts</h3>
            <div className="lb-facts">
              <div className="lb-fact-row"><span>2025 Total Corporate Lobbying</span><span className="lb-fact-val">$4.1B</span></div>
              <div className="lb-fact-row"><span>YoY Increase</span><span className="lb-fact-val positive">+6.2%</span></div>
              <div className="lb-fact-row"><span>Active Registrants</span><span className="lb-fact-val">12,400+</span></div>
              <div className="lb-fact-row"><span>Fastest Growing Issue</span><span className="lb-fact-val highlight">AI Regulation</span></div>
              <div className="lb-fact-row"><span>AI Lobbying Growth</span><span className="lb-fact-val positive">+340%</span></div>
              <div className="lb-fact-row"><span>Top Sector</span><span className="lb-fact-val">Defense</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}